(function (root, factory) {
  const preview = factory();

  if (typeof module === 'object' && module.exports) {
    module.exports = preview;
  }

  root.FarmFetchFreshFrozenPreview = preview;

  if (!root.document) return;

  const mount = () => preview.mountAll(root.document);

  if (root.document.readyState === 'loading') {
    root.document.addEventListener('DOMContentLoaded', mount);
  } else {
    mount();
  }

  root.document.addEventListener('shopify:section:load', mount);
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  const PACK_SIZE = 16;
  const STEP = 4;
  const MAX_PROTEIN_PACKS = PACK_SIZE - STEP;
  const PROTEINS = Object.freeze(['beef', 'chicken', 'pork', 'fish', 'turkey']);
  const DEFAULT_COMPOSITION = Object.freeze({
    beef: 4,
    chicken: 4,
    pork: 4,
    fish: 4,
    turkey: 0
  });

  function totalPacks(composition) {
    return PROTEINS.reduce((total, protein) => total + Number(composition[protein] || 0), 0);
  }

  function hasValidQuantities(composition) {
    return PROTEINS.every((protein) => {
      const quantity = Number(composition[protein]);
      return Number.isInteger(quantity) && quantity >= 0 && quantity <= MAX_PROTEIN_PACKS && quantity % STEP === 0;
    });
  }

  function isCompleteComposition(composition) {
    return hasValidQuantities(composition) && totalPacks(composition) === PACK_SIZE;
  }

  function canAdjust(composition, protein, change) {
    if (!PROTEINS.includes(protein) || ![STEP, -STEP].includes(change)) return false;
    if (!hasValidQuantities(composition)) return false;

    const nextQuantity = composition[protein] + change;
    const nextTotal = totalPacks(composition) + change;

    return nextQuantity >= 0 && nextQuantity <= MAX_PROTEIN_PACKS && nextTotal >= 0 && nextTotal <= PACK_SIZE;
  }

  function adjustComposition(composition, protein, change) {
    const next = { ...composition };
    if (!canAdjust(composition, protein, change)) return next;

    next[protein] += change;
    return next;
  }

  function cloneDefaultComposition() {
    return { ...DEFAULT_COMPOSITION };
  }

  function calculateProductSubtotal(basePrice, planPrices, sellingPlanId, quantity) {
    const baseUnitPrice = Number(basePrice);
    const parsedQuantity = Number(quantity);
    const safeQuantity = Number.isInteger(parsedQuantity) && parsedQuantity >= 1 ? parsedQuantity : 1;
    const planUnitPrice = planPrices && sellingPlanId ? Number(planPrices[String(sellingPlanId)]) : NaN;
    const unitPrice = Number.isFinite(planUnitPrice) && planUnitPrice >= 0 ? planUnitPrice : baseUnitPrice;
    if (!Number.isFinite(unitPrice) || unitPrice < 0) return 0;
    return Math.round(unitPrice * safeQuantity);
  }

  function formatProductSubtotal(cents, currency, locale) {
    const currencyCode = currency || 'CAD';
    const formatted = new Intl.NumberFormat(locale || 'en-CA', {
      style: 'currency',
      currency: currencyCode,
      currencyDisplay: 'narrowSymbol'
    }).format(Number(cents || 0) / 100);
    return formatted.includes(currencyCode) ? formatted : `${formatted} ${currencyCode}`;
  }

  function cartAddUrl(view) {
    const root = view && view.Shopify && view.Shopify.routes && view.Shopify.routes.root
      ? view.Shopify.routes.root
      : '/';
    return root.replace(/\/?$/, '/') + 'cart/add.js';
  }

  function cartSections(view) {
    try {
      const helper = view.Alpine && view.Alpine.store ? view.Alpine.store('xCartHelper') : null;
      return helper && helper.getSectionsToRender
        ? helper.getSectionsToRender().map((section) => section.id)
        : [];
    } catch (error) {
      return [];
    }
  }

  async function addProductFormToCart(view, form) {
    const formData = new view.FormData(form);
    const sections = cartSections(view);
    if (sections.length > 0) formData.append('sections', sections);
    formData.append('sections_url', view.location.pathname);

    const response = await view.fetch(cartAddUrl(view), {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: formData
    });
    let payload = {};
    try {
      payload = await response.json();
    } catch (error) {}
    if (!response.ok) {
      throw new Error(payload.description || payload.message || 'Shopify could not add this product to the cart.');
    }
    return payload;
  }

  function productCartMessage(form) {
    let message = form.querySelector('[data-ff-product-cart-message]');
    if (message) return message;
    message = form.ownerDocument.createElement('p');
    message.className = 'ff-product-cart-message';
    message.setAttribute('role', 'status');
    message.setAttribute('aria-live', 'polite');
    message.dataset.ffProductCartMessage = '';
    form.appendChild(message);
    return message;
  }

  async function refreshProductCart(view, payload) {
    try {
      const helper = view.Alpine && view.Alpine.store ? view.Alpine.store('xCartHelper') : null;
      if (helper && helper.reRenderSections) {
        if (payload.sections) {
          await helper.reRenderSections(payload.sections);
        } else {
          await helper.reRenderSections();
        }
      }
      if (helper && payload.item_count != null) helper.currentItemCount = Number(payload.item_count);
      const miniCart = view.Alpine && view.Alpine.store ? view.Alpine.store('xMiniCart') : null;
      if (miniCart && miniCart.openCart) miniCart.openCart();
    } catch (error) {}
    view.document.dispatchEvent(new view.CustomEvent('eurus:cart:items-changed'));
  }

  function commonSellingPlans(planLists) {
    if (!Array.isArray(planLists) || planLists.length !== PROTEINS.length) return [];
    if (planLists.some((plans) => !Array.isArray(plans) || plans.length === 0)) return [];

    const remainingPlanIds = planLists.slice(1).map((plans) => new Set(plans.map((plan) => String(plan.id))));
    return planLists[0].filter((plan) =>
      remainingPlanIds.every((planIds) => planIds.has(String(plan.id)))
    );
  }

  function stableBoxId(identity, sellingPlanId, components) {
    const parts = [identity || 'farmfetch-box', sellingPlanId || 'one-time']
      .concat((components || []).map((component) => `${component.variantId}:${component.quantity}`).sort());
    return `ff-${parts.join('|').toLowerCase().replace(/[^a-z0-9|:_-]+/g, '-').slice(0, 240)}`;
  }

  function componentProtein(component) {
    const handle = String(component && component.handle || '');
    const protein = handle.split('-').pop();
    const labels = { chic: 'Chicken', chik: 'Chicken' };
    return labels[protein] || (protein ? protein.charAt(0).toUpperCase() + protein.slice(1) : '');
  }

  function buildCartItems(composition, variantIds, sellingPlanId, boxId, boxQuantity) {
    if (!isCompleteComposition(composition)) return [];
    const parsedSellingPlanId = sellingPlanId ? Number(sellingPlanId) : null;
    const parsedBoxQuantity = Number(boxQuantity || 1);
    if (!Number.isInteger(parsedBoxQuantity) || parsedBoxQuantity < 1) return [];
    if (sellingPlanId && (!Number.isInteger(parsedSellingPlanId) || parsedSellingPlanId <= 0)) return [];
    const configured = PROTEINS.every((protein) => {
      const quantity = Number(composition[protein]);
      const variantId = Number(variantIds[protein]);
      return quantity === 0 || (Number.isInteger(variantId) && variantId > 0);
    });
    if (!configured) return [];

    return PROTEINS.reduce((items, protein) => {
      const quantity = Number(composition[protein]);
      const variantId = Number(variantIds[protein]);
      if (quantity === 0) return items;
      const item = {
        id: variantId,
        quantity: quantity * parsedBoxQuantity,
        properties: {
          '_FarmFetch box label': 'Fresh Frozen 16-Pack Variety',
          '_FarmFetch display title': 'Fresh Frozen 16-Pack Variety',
          Protein: protein.charAt(0).toUpperCase() + protein.slice(1),
          '_FarmFetch box ID': boxId || '16-pack-variety',
          '_FarmFetch box quantity': parsedBoxQuantity,
          '_FarmFetch units per box': quantity,
          '_FarmFetch box URL': '/collections/ffm-16-box#variety'
        }
      };

      if (parsedSellingPlanId) item.selling_plan = parsedSellingPlanId;
      items.push(item);
      return items;
    }, []);
  }

  function commonComponentSellingPlans(components) {
    if (!Array.isArray(components) || components.length === 0) return [];
    const planLists = components.map((component) => Array.isArray(component.plans) ? component.plans : []);
    if (planLists.some((plans) => plans.length === 0)) return [];
    const remaining = planLists.slice(1).map((plans) => new Set(plans.map((plan) => String(plan.id))));
    return planLists[0].filter((plan) => remaining.every((ids) => ids.has(String(plan.id))));
  }

  function componentPlanPrice(component, sellingPlanId) {
    const plan = Array.isArray(component.plans)
      ? component.plans.find((candidate) => String(candidate.id) === String(sellingPlanId))
      : null;
    const price = plan && plan.price != null ? Number(plan.price) : Number(component.basePrice);
    return Number.isFinite(price) && price >= 0 ? price : NaN;
  }

  function calculateComponentSubtotal(components, sellingPlanId, quantity) {
    if (!Array.isArray(components) || components.length === 0) return 0;
    const multiplier = Number(quantity);
    if (!Number.isInteger(multiplier) || multiplier < 1) return 0;
    const total = components.reduce((sum, component) => {
      const unitPrice = componentPlanPrice(component, sellingPlanId);
      const componentQuantity = Number(component.quantity);
      if (!Number.isFinite(unitPrice) || !Number.isInteger(componentQuantity) || componentQuantity < 1) return NaN;
      return sum + (unitPrice * componentQuantity * multiplier);
    }, 0);
    return Number.isFinite(total) ? Math.round(total) : 0;
  }

  function buildComponentItems(config, sellingPlanId, quantity, boxId) {
    if (!config || !Array.isArray(config.components) || config.components.length === 0) return [];
    const parsedPlanId = sellingPlanId ? Number(sellingPlanId) : null;
    const multiplier = Number(quantity);
    if (!Number.isInteger(multiplier) || multiplier < 1) return [];
    if (sellingPlanId && (!Number.isInteger(parsedPlanId) || parsedPlanId <= 0)) return [];
    if (parsedPlanId) {
      const plans = commonComponentSellingPlans(config.components);
      if (!plans.some((plan) => String(plan.id) === String(parsedPlanId))) return [];
    }

    return config.components.map((component) => {
      const variantId = Number(component.variantId);
      const componentQuantity = Number(component.quantity);
      if (!Number.isInteger(variantId) || variantId <= 0 || component.available !== true || !Number.isInteger(componentQuantity) || componentQuantity < 1) return null;
      const properties = {
        '_FarmFetch box label': config.boxLabel || 'Fresh Topper Variety Pack',
        '_FarmFetch display title': config.displayTitle || config.boxLabel || 'Fresh Topper Variety Pack',
        '_FarmFetch box ID': boxId || 'fresh-topper-variety',
        '_FarmFetch box quantity': multiplier,
        '_FarmFetch units per box': componentQuantity,
        '_FarmFetch box URL': config.boxUrl || '/collections/fdt'
      };
      if (config.components.length > 1) properties.Protein = componentProtein(component);
      const item = {
        id: variantId,
        quantity: componentQuantity * multiplier,
        properties
      };
      if (parsedPlanId) item.selling_plan = parsedPlanId;
      return item;
    }).filter(Boolean);
  }

  const buildComponentSubscriptionItems = buildComponentItems;

  async function addItemsToCart(fetchImplementation, cartAddUrl, items, sections, sectionsUrl) {
    const request = { items };
    if (sections && sections.length) {
      request.sections = sections.join(',');
      request.sections_url = sectionsUrl || '/';
    }
    const response = await fetchImplementation(cartAddUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify(request)
    });

    let payload = {};
    try {
      payload = await response.json();
    } catch (error) {
      payload = {};
    }

    if (!response.ok) {
      throw new Error(payload.description || payload.message || 'Shopify could not add this box to the cart.');
    }

    return payload;
  }

  function addPreviewThemeId(url, currentSearch) {
    const previewThemeId = new URLSearchParams(currentSearch || '').get('preview_theme_id');
    if (!previewThemeId || !/^\d+$/.test(previewThemeId)) return url;

    const parsedUrl = new URL(url, 'https://farmfetch-preview.invalid');
    parsedUrl.searchParams.set('preview_theme_id', previewThemeId);

    if (/^[a-z][a-z\d+.-]*:/i.test(url) || url.startsWith('//')) {
      return parsedUrl.href;
    }

    return `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
  }

  function preservePreviewThemeLinks(documentRoot) {
    const currentSearch = documentRoot.defaultView
      ? documentRoot.defaultView.location.search
      : '';

    documentRoot.querySelectorAll('[data-ff-preview-link]').forEach((link) => {
      const href = link.getAttribute('href');
      if (href) link.setAttribute('href', addPreviewThemeId(href, currentSearch));
    });
  }

  function mountPreview(section) {
    if (!section || section.dataset.ffPreviewMounted === 'true') return;
    section.dataset.ffPreviewMounted = 'true';

    const tabButtons = Array.from(section.querySelectorAll('[data-ff-preview-tab]'));
    const panels = Array.from(section.querySelectorAll('[data-ff-preview-panel]'));
    const rows = Array.from(section.querySelectorAll('[data-ff-preview-protein]'));
    const resetButton = section.querySelector('[data-ff-preview-reset]');
    const totalOutput = section.querySelector('[data-ff-preview-total]');
    const statusOutput = section.querySelector('[data-ff-preview-status]');
    const progress = section.querySelector('[data-ff-preview-progress]');
    const addToCartButton = section.querySelector('[data-ff-preview-add-to-cart]');
    const cartMessage = section.querySelector('[data-ff-preview-cart-message]');
    const subscriptionRadio = section.querySelector('[data-ff-preview-subscription-radio]');
    const purchaseRadios = Array.from(section.querySelectorAll('[data-ff-preview-purchase-options] input[type="radio"]'));
    const planField = section.querySelector('[data-ff-preview-plan-field]');
    const planSelect = section.querySelector('[data-ff-preview-selling-plan]');
    const subscriptionStatus = section.querySelector('[data-ff-preview-subscription-status]');
    const summaryPrice = section.querySelector('[data-ff-preview-summary-price]');
    const summaryPerPack = section.querySelector('[data-ff-preview-summary-per-pack]');
    const boxQuantityInput = section.querySelector('[data-ff-preview-box-quantity]');
    const oneTimePrice = section.querySelector('[data-ff-preview-one-time-price]');
    const subscriptionWelcome = section.querySelector("[data-ff-subscription-welcome]");

    const componentData = rows.reduce((result, row) => {
      const plansElement = row.querySelector('[data-ff-preview-selling-plans]');
      let plans = [];
      try {
        plans = JSON.parse(plansElement ? plansElement.textContent : '[]');
      } catch (error) {
        plans = [];
      }

      result[row.dataset.ffPreviewProtein] = {
        variantId: Number(row.dataset.variantId),
        available: row.dataset.componentAvailable === 'true',
        basePrice: Number(row.dataset.basePrice),
        plans
      };
      return result;
    }, {});
    const variantIds = PROTEINS.reduce((result, protein) => {
      result[protein] = componentData[protein] ? componentData[protein].variantId : 0;
      return result;
    }, {});
    const sharedPlans = commonSellingPlans(PROTEINS.map((protein) =>
      componentData[protein] ? componentData[protein].plans : []
    ));
    const variantsConfigured = PROTEINS.every((protein) =>
      Number.isInteger(variantIds[protein]) && variantIds[protein] > 0 && componentData[protein].available
    );
    let isAddingToCart = false;

    let composition = rows.reduce((result, row) => {
      const protein = row.dataset.ffPreviewProtein;
      result[protein] = Number(row.dataset.defaultQuantity);
      return result;
    }, {});

    function selectTab(tabName, moveFocus) {
      tabButtons.forEach((button) => {
        const isActive = button.dataset.ffPreviewTab === tabName;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-selected', String(isActive));
        button.tabIndex = isActive ? 0 : -1;
        if (isActive && moveFocus) button.focus();
      });

      panels.forEach((panel) => {
        panel.hidden = panel.dataset.ffPreviewPanel !== tabName;
      });
    }

    function selectTabFromHash() {
      const hash = section.ownerDocument.defaultView?.location.hash;
      if (hash !== '#variety') return;
      selectTab('variety', false);
      section.querySelector('[data-ff-variety-anchor]')?.scrollIntoView({ block: 'start' });
    }

    function compositionSubtotal(sellingPlanId) {
      const boxQuantity = boxQuantityInput ? Math.max(1, Number(boxQuantityInput.value) || 1) : 1;
      const subtotal = PROTEINS.reduce((sum, protein) => {
        const component = componentData[protein];
        const quantity = Number(composition[protein] || 0);
        if (!component || quantity === 0) return sum;
        const unitPrice = sellingPlanId ? componentPlanPrice(component, sellingPlanId) : component.basePrice;
        return Number.isFinite(unitPrice) ? sum + (unitPrice * quantity) : NaN;
      }, 0);
      return Number.isFinite(subtotal) ? Math.round(subtotal * boxQuantity) : 0;
    }

    function renderComposition() {
      const total = totalPacks(composition);
      const remaining = PACK_SIZE - total;

      rows.forEach((row) => {
        const protein = row.dataset.ffPreviewProtein;
        const quantityOutput = row.querySelector('[data-ff-preview-quantity]');
        const minusButton = row.querySelector('[data-ff-preview-adjust="-1"]');
        const plusButton = row.querySelector('[data-ff-preview-adjust="1"]');
        const summaryQuantity = section.querySelector(`[data-ff-preview-summary="${protein}"] strong`);

        if (quantityOutput) quantityOutput.textContent = String(composition[protein]);
        if (summaryQuantity) summaryQuantity.textContent = String(composition[protein]);
        if (minusButton) minusButton.disabled = !canAdjust(composition, protein, -STEP);
        if (plusButton) plusButton.disabled = !canAdjust(composition, protein, STEP);
      });

      if (totalOutput) totalOutput.textContent = String(total);
      if (progress) progress.style.width = `${Math.min(100, (total / PACK_SIZE) * 100)}%`;

      const selectedPurchase = purchaseRadios.find((radio) => radio.checked);
      const selectedPlanId = selectedPurchase && selectedPurchase.value === 'subscription' && planSelect ? planSelect.value : '';
      const subtotal = compositionSubtotal(selectedPlanId);
      const boxQuantity = boxQuantityInput ? Math.max(1, Number(boxQuantityInput.value) || 1) : 1;
      const currency = section.dataset.currency || 'CAD';
      const locale = section.dataset.locale || 'en-CA';
      if (summaryPrice) summaryPrice.textContent = formatProductSubtotal(subtotal, currency, locale);
      if (summaryPerPack) summaryPerPack.textContent = `${formatProductSubtotal(Math.round(subtotal / (PACK_SIZE * boxQuantity)), currency, locale)} per 250 g pack`;
      if (oneTimePrice && !selectedPlanId) oneTimePrice.textContent = formatProductSubtotal(subtotal, currency, locale);
      if (subscriptionWelcome) subscriptionWelcome.hidden = selectedPurchase && selectedPurchase.value !== "subscription";

      if (statusOutput) {
        const complete = isCompleteComposition(composition);
        statusOutput.classList.toggle('is-complete', complete);
        statusOutput.textContent = complete
          ? 'Box complete'
          : `Add ${remaining} packs`;
      }

      if (addToCartButton) {
        const selectedPurchase = purchaseRadios.find((radio) => radio.checked);
        const subscriptionUnavailable = selectedPurchase && selectedPurchase.value === 'subscription' && sharedPlans.length === 0;
        addToCartButton.disabled = !isCompleteComposition(composition) || !variantsConfigured || isAddingToCart || subscriptionUnavailable;
      }
    }

    if (planSelect) {
      sharedPlans.forEach((plan) => {
        const option = section.ownerDocument.createElement('option');
        option.value = String(plan.id);
        option.textContent = plan.name;
        planSelect.appendChild(option);
      });
    }

    if (subscriptionRadio && sharedPlans.length > 0) {
      subscriptionRadio.disabled = false;
      if (subscriptionStatus) subscriptionStatus.hidden = true;
    }

    purchaseRadios.forEach((radio) => {
      radio.addEventListener('change', () => {
        const isSubscription = radio.checked && radio.value === 'subscription';
        if (planField) planField.hidden = !isSubscription;
        renderComposition();
      });
    });

    tabButtons.forEach((button, index) => {
      button.addEventListener('click', () => selectTab(button.dataset.ffPreviewTab, false));
      button.addEventListener('keydown', (event) => {
        let nextIndex = null;

        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
          nextIndex = (index + 1) % tabButtons.length;
        } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
          nextIndex = (index - 1 + tabButtons.length) % tabButtons.length;
        } else if (event.key === 'Home') {
          nextIndex = 0;
        } else if (event.key === 'End') {
          nextIndex = tabButtons.length - 1;
        }

        if (nextIndex === null) return;
        event.preventDefault();
        selectTab(tabButtons[nextIndex].dataset.ffPreviewTab, true);
      });
    });

    rows.forEach((row) => {
      row.querySelectorAll('[data-ff-preview-adjust]').forEach((button) => {
        button.addEventListener('click', () => {
          composition = adjustComposition(
            composition,
            row.dataset.ffPreviewProtein,
            Number(button.dataset.ffPreviewAdjust)
          );
          renderComposition();
        });
      });
    });

    if (resetButton) {
      resetButton.addEventListener('click', () => {
        composition = cloneDefaultComposition();
        renderComposition();
      });
    }

    if (boxQuantityInput) {
      boxQuantityInput.addEventListener("input", renderComposition);
      boxQuantityInput.addEventListener("change", renderComposition);
    }
    if (addToCartButton) {
      addToCartButton.addEventListener('click', async () => {
        const selectedPurchase = purchaseRadios.find((radio) => radio.checked);
        const sellingPlanId = selectedPurchase && selectedPurchase.value === 'subscription' && planSelect
          ? planSelect.value
          : '';
        const view = section.ownerDocument.defaultView;
        const boxId = stableBoxId('ffm-16-variety', sellingPlanId, PROTEINS.map((protein) => ({
          variantId: variantIds[protein],
          quantity: composition[protein]
        })));
        const boxQuantity = boxQuantityInput ? Math.max(1, Number(boxQuantityInput.value) || 1) : 1;
        const items = buildCartItems(composition, variantIds, sellingPlanId, boxId, boxQuantity);

        if (items.length === 0) {
          if (cartMessage) cartMessage.textContent = 'Complete all 16 packs before adding this box.';
          return;
        }

        isAddingToCart = true;
        addToCartButton.textContent = 'Adding your box…';
        if (cartMessage) cartMessage.textContent = '';
        renderComposition();

        try {
          await addItemsToCart(
            view.fetch.bind(view),
            section.dataset.cartAddUrl || '/cart/add.js',
            items,
            cartSections(view),
            view.location.pathname
          );
          if (cartMessage) cartMessage.textContent = 'Your 16-pack box was added. Opening the cart…';
          view.location.assign(section.dataset.cartUrl || '/cart');
        } catch (error) {
          isAddingToCart = false;
          addToCartButton.textContent = 'Add 16-pack variety to cart';
          if (cartMessage) cartMessage.textContent = error.message;
          renderComposition();
        }
      });
    }

    renderComposition();
    selectTabFromHash();
    section.ownerDocument.defaultView?.addEventListener('hashchange', selectTabFromHash);
  }

  function mountProductPreview(section) {
    if (!section || section.dataset.ffProductPreviewMounted === 'true') return;
    section.dataset.ffProductPreviewMounted = 'true';

    const mainImage = section.querySelector('[data-ff-product-main-image]');
    const thumbnails = Array.from(section.querySelectorAll('[data-ff-product-thumbnail]'));
    const form = section.querySelector('.ff-frozen-product-preview__form');
    const quantityInput = section.querySelector('[data-ff-product-quantity]');
    const subtotalOutput = section.querySelector('[data-ff-product-subtotal]');
    const firstOrderGift = section.querySelector("[data-ff-first-order-gift]");
    const subscriptionWelcome = section.querySelector("[data-ff-subscription-welcome]");
    const priceDataElement = section.querySelector('[data-ff-product-prices]');
    const sealTarget = section.querySelector('#sealsubscriptions-default-widget-target-element');
    let priceData = {};

    try {
      priceData = priceDataElement ? JSON.parse(priceDataElement.textContent) : {};
    } catch (error) {
      priceData = {};
    }

    let componentConfig = null;
    const componentDataElement = section.querySelector('[data-ff-component-subscription]');
    try {
      componentConfig = componentDataElement ? JSON.parse(componentDataElement.textContent) : null;
    } catch (error) {
      componentConfig = null;
    }
    function selectedSellingPlanId() {
      if (!form) return null;
      const fields = Array.from(form.querySelectorAll('[name="selling_plan"]'));
      const selected = fields.find((field) => {
        if (!field.value || field.disabled) return false;
        return !['radio', 'checkbox'].includes(field.type) || field.checked;
      });
      return selected ? selected.value : null;
    }

    function renderSubtotal() {
      if (!subtotalOutput) return;
      const sellingPlanId = selectedSellingPlanId();
      const quantity = quantityInput ? quantityInput.value : 1;
      const subtotal = componentConfig && sellingPlanId
        ? calculateComponentSubtotal(componentConfig.components, sellingPlanId, quantity)
        : calculateProductSubtotal(priceData.basePrice, priceData.plans, sellingPlanId, quantity);
      subtotalOutput.textContent = formatProductSubtotal(subtotal, priceData.currency, priceData.locale);
      if (firstOrderGift) firstOrderGift.hidden = Boolean(sellingPlanId);
      if (subscriptionWelcome) subscriptionWelcome.hidden = !sellingPlanId;

    }
    if (quantityInput) {
      quantityInput.addEventListener('input', renderSubtotal);
      quantityInput.addEventListener('change', renderSubtotal);
    }
    if (form) {
      form.addEventListener('change', renderSubtotal);
      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        if (form.dataset.ffProductAdding === 'true') return;

        const view = section.ownerDocument.defaultView;
        const submitButton = form.querySelector('[type="submit"]');
        const message = productCartMessage(form);
        const originalLabel = submitButton ? submitButton.textContent : '';
        form.dataset.ffProductAdding = 'true';
        if (submitButton) {
          submitButton.disabled = true;
          submitButton.textContent = 'Adding to cart...';
        }
        message.classList.remove('is-error');
        message.textContent = '';

        try {
          const sellingPlanId = selectedSellingPlanId();
          let payload;
          if (componentConfig && (section.dataset.ffComponentCheckout === 'always' || sellingPlanId)) {
            const boxId = stableBoxId(componentConfig.boxUrl || componentConfig.boxLabel, sellingPlanId, componentConfig.components);
            const items = buildComponentItems(
              componentConfig,
              sellingPlanId,
              quantityInput ? quantityInput.value : 1,
              boxId
            );
            if (items.length !== componentConfig.components.length) {
              throw new Error('One or more variety components are unavailable.');
            }
            payload = await addItemsToCart(
              view.fetch.bind(view),
              cartAddUrl(view),
              items,
              cartSections(view),
              view.location.pathname
            );
          } else {
            payload = await addProductFormToCart(view, form);
          }
          await refreshProductCart(view, payload);
          message.textContent = 'Added to cart.';
        } catch (error) {
          message.classList.add('is-error');
          message.textContent = error.message || 'We could not add this product to the cart.';
        } finally {
          form.dataset.ffProductAdding = 'false';
          if (submitButton) {
            submitButton.disabled = false;
            submitButton.textContent = originalLabel;
          }
        }
      });
    }
    if (sealTarget && typeof globalThis.MutationObserver === 'function') {
      let subtotalFrame = 0;
      const scheduleSubtotalRender = () => {
        if (subtotalFrame) return;
        subtotalFrame = globalThis.requestAnimationFrame(() => {
          subtotalFrame = 0;
          renderSubtotal();
        });
      };
      const observer = new globalThis.MutationObserver(scheduleSubtotalRender);
      observer.observe(sealTarget, { childList: true, subtree: true, attributes: true });
    }
    renderSubtotal();

    if (!mainImage || thumbnails.length === 0) return;

    thumbnails.forEach((thumbnail) => {
      thumbnail.addEventListener('click', () => {
        mainImage.src = thumbnail.dataset.imageSrc;
        mainImage.alt = thumbnail.dataset.imageAlt || '';

        thumbnails.forEach((candidate) => {
          const isActive = candidate === thumbnail;
          candidate.classList.toggle('is-active', isActive);
          candidate.setAttribute('aria-selected', String(isActive));
        });
      });
    });
  }

  function mountQuickAdd(form) { if (!form || form.dataset.ffQuickAddMounted === 'true') return; form.dataset.ffQuickAddMounted = 'true'; form.addEventListener('submit', async (event) => { event.preventDefault(); if (form.dataset.ffQuickAddAdding === 'true') return; const view = form.ownerDocument.defaultView; const button = form.querySelector('[type=submit]'); const message = form.querySelector('[data-ff-quick-add-message]'); const label = button ? button.textContent : ''; form.dataset.ffQuickAddAdding = 'true'; if (button) { button.disabled = true; button.textContent = 'Adding…'; } if (message) { message.hidden = true; message.textContent = ''; message.classList.remove('is-error'); } try { let payload; const componentVariantId = Number(form.dataset.ffComponentVariantId); if (Number.isInteger(componentVariantId) && componentVariantId > 0) { const componentQuantity = Number(form.dataset.ffComponentQuantity) || 16; payload = await addItemsToCart(view.fetch.bind(view), cartAddUrl(view), [{ id: componentVariantId, quantity: componentQuantity, properties: { '_FarmFetch display title': form.dataset.ffComponentTitle || '', '_FarmFetch box ID': stableBoxId(form.dataset.ffComponentBoxUrl || form.dataset.ffComponentTitle || 'ffm-16-box', null, [{ variantId: componentVariantId, quantity: componentQuantity }]), '_FarmFetch box quantity': 1, '_FarmFetch units per box': componentQuantity, '_FarmFetch box URL': form.dataset.ffComponentBoxUrl || '/collections/ffm-16-box' } }], cartSections(view), view.location.pathname); } else { payload = await addProductFormToCart(view, form); } await refreshProductCart(view, payload); if (message) { message.textContent = 'Added to cart.'; message.hidden = false; } } catch (error) { if (message) { message.textContent = error.message || 'We could not add this product to the cart.'; message.hidden = false; message.classList.add('is-error'); } } finally { form.dataset.ffQuickAddAdding = 'false'; if (button) { button.disabled = false; button.textContent = label; } } }); }
  function mountAll(documentRoot) {
    documentRoot.querySelectorAll('[data-ff-frozen-preview]').forEach(mountPreview);
    documentRoot.querySelectorAll('[data-ff-frozen-product-preview]').forEach(mountProductPreview);
    documentRoot.querySelectorAll('[data-ff-quick-add]').forEach(mountQuickAdd);
    preservePreviewThemeLinks(documentRoot);
  }

  return Object.freeze({
    PACK_SIZE,
    STEP,
    PROTEINS,
    DEFAULT_COMPOSITION,
    totalPacks,
    hasValidQuantities,
    isCompleteComposition,
    canAdjust,
    adjustComposition,
    cloneDefaultComposition,
    calculateProductSubtotal,
    formatProductSubtotal,
    commonSellingPlans,
    stableBoxId,
    buildCartItems,
    commonComponentSellingPlans,
    calculateComponentSubtotal,
    buildComponentItems,
    buildComponentSubscriptionItems,
    addItemsToCart,
    addProductFormToCart,
    cartAddUrl,
    addPreviewThemeId,
    mountPreview,
    mountProductPreview,
    mountQuickAdd,
    mountAll
  });
});
